import { toHumanReadable } from '@core/logic';
import { useIsTelekomProject } from '@core/logic/hooks/useIsTelekomProject';
import { useAppDispatch } from '@core/redux/hooks';
import type { FullAddress } from '@core/types/address';
import { organizationManagementApiV1 as organizationManagementApi } from '@deepup/apis';
import { ElementDialogProps, ParentSelectAutocomplete } from '@elements/components';
import { elementSelectors } from '@elements/redux';
import { pointElementSelectors, pointElementThunks } from '@elements/redux/point-element.slice';
import {
  COMMENT_PREFIX,
  NO_GEO,
  PartialPointEl,
  PointElement,
  PointElementSubtype,
  pointElementSubtype,
  pointElementSubtypeAbbreviations,
} from '@elements/types';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  Button,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid2 as Grid,
  ListItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { depthsSorted, surfaceTypes, widths } from '@trenches/types';
import { viewer3dActions, viewer3dSelectors } from '@viewer3D/redux';
import { set } from 'lodash';
import { Fragment, useState } from 'react';
import { useSelector } from 'react-redux';
import MaterialSelectAutocomplete from './MaterialSelectAutocomplete';
import { namingSchemes } from './namingSchemesCustomers';
import { useMaterials } from './useMaterials';
import { extractFlatMappings, useOrderNumbers } from './useOrderNumbers';

const initialAddress: FullAddress = {
  street: '',
  number: undefined,
  zip: '',
  city: '',
};

const intialPointElementState: PartialPointEl = {
  kind: 'NETWORK_NODE',
  name: '',
  subtype: 'HAUPTVERTEILER',
  parentPointElementId: null,
  pointId: null,
  address: initialAddress,
  smaNumber: null,
  materialId: null,
};

const NameTextField = ({
  element,
  setName,
  namingScheme,
  hasNameError = false,
  warningMessage,
  ...autocompleteProps
}: {
  element: PartialPointEl;
  setName: (nextVal: string) => void;
  namingScheme?: RegExp;
  hasNameError?: boolean;
  warningMessage?: string;
} & Partial<AutocompleteRenderInputParams>) => {
  const hasWarning = warningMessage && warningMessage.trim() !== '';

  return (
    <TextField
      label="Name"
      inputProps={{ 'aria-label': 'Name' }}
      fullWidth
      data-testid="element-name-input"
      required={!!namingScheme}
      value={element.name ?? ''}
      onChange={(e) => setName(e.target.value)}
      error={namingScheme && hasNameError}
      helperText={
        hasWarning
          ? warningMessage
          : namingScheme &&
            hasNameError &&
            `Please enter a name for ${toHumanReadable(
              element.subtype,
            )} in the following regex-format: ${namingScheme.source.replace(/\^\$/, '')}`
      }
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...autocompleteProps}
      sx={
        hasWarning
          ? {
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'warning.main',
                },
                '&:hover fieldset': {
                  borderColor: 'warning.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'warning.main',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'warning.main',
                '&.Mui-focused': {
                  color: 'warning.main',
                },
              },
            }
          : undefined
      }
    />
  );
};

export const useConnect = ({ elementToUpdate }: { elementToUpdate?: PointElement }) => {
  const dispatch = useAppDispatch();
  const hasPointChildren = useSelector(
    pointElementSelectors.findFirst((p) => p.parentPointElementId === elementToUpdate?.id),
  );
  const hasSplineChildren = useSelector(
    elementSelectors.findFirst((p) => p.parentPointElementId === elementToUpdate?.id),
  );
  const hasChildren = hasPointChildren || hasSplineChildren;

  const [element, setElement] = useState<PartialPointEl>(
    elementToUpdate ?? intialPointElementState,
  );

  const project = useSelector(viewer3dSelectors.selectedProject)!;

  const updateProp = (path: string, value: unknown) => {
    if (path === 'subtype') {
      setElement((s) => set({ ...s, name: '' }, path, value));
    } else {
      setElement((s) => set({ ...s, address: { ...s.address } }, path, value));
    }
  };

  const addElement = async () => {
    const { payload } = await dispatch(
      pointElementThunks.post({
        path: ['projects', project.id, 'pointElements'],
        body: { ...element, projectId: project.id },
      }),
    );

    return payload as PointElement;
  };

  const updateElement = async () => {
    const { payload } = await dispatch(
      pointElementThunks.patch({
        path: ['projects', project.id, 'pointElements', elementToUpdate!.id],
        body: element,
      }),
    );
    return payload as PointElement;
  };

  const onSubmit = async () => {
    if (element === intialPointElementState) return;
    const nextElement = await (elementToUpdate ? updateElement : addElement)();

    // reselect element to navigate to it again (dirty hack!)
    dispatch(viewer3dActions.setSelectedElementIds([]));
    dispatch(viewer3dActions.setSelectedElementIds([nextElement.id]));

    return nextElement;
  };

  const elements = useSelector(pointElementSelectors.selectAll);

  const doesElementExistWithSameName = (isUpdatingElement: boolean, element: PartialPointEl) => {
    if (element.name === '') return false;
    return elements.filter((e) => e.name === element.name).length > (isUpdatingElement ? 1 : 0);
  };

  const autocompleteOptions = project.metadata.filter((d) => d.subtype === element.subtype);

  const namingScheme = project.exportTarget
    ? namingSchemes[project.exportTarget][element.subtype]
    : undefined;

  const hasNameError =
    // ATTENTION: need to explicitly check for name !== undefined, because an empty string is falsy
    !!namingScheme && element.name !== undefined && !namingScheme.test(element.name);

  const isAddressValid =
    element.subtype === 'HAUPTKABEL' || // Hauptkabel doesn't need an address
    element.subtype === 'BACKBONE' || // Backbone doesn't need an address
    element.subtype === 'KABELSCHACHT' || // Kabelschacht doesn't need an address
    element.subtype === 'POLE' || // Pole doesn't need an address
    element.subtype === 'PIT' || // Pit doesn't need an address
    element.subtype === 'JUNCTION_BOX' || // Junction box doesn't need an address
    element.subtype === 'GLASFASER_MUFFE' || // Glasfaser Muffe doesn't need an address
    (element.address.number !== undefined &&
      !isNaN(element.address.number) &&
      element.address.city !== initialAddress.city &&
      element.address.zip !== initialAddress.zip &&
      element.address.street !== initialAddress.street);

  return {
    hasChildren,
    element,
    updateProp,
    onSubmit,
    autocompleteOptions,
    namingScheme,
    hasNameError,
    isAddressValid,
    doesElementExistWithSameName,
  };
};

const getCommentToAdd = (isNoGeoChecked: boolean, comment?: string) => {
  const parts = [];
  if (isNoGeoChecked) parts.push(NO_GEO);
  if (comment) parts.push(`${COMMENT_PREFIX}${comment}`);

  return parts.join('.');
};

const extractCommentFromCommentField = (commentFieldValue?: string | null): string | undefined => {
  if (!commentFieldValue) {
    return undefined;
  }

  const commentProperties = commentFieldValue.split('.');

  const comment = commentProperties.find((p) => p.startsWith(COMMENT_PREFIX));

  return comment?.replaceAll(COMMENT_PREFIX, '');
};

const extractNoGeoFromCommentField = (commentFieldValue?: string | null): boolean => {
  return commentFieldValue?.includes(NO_GEO) ?? false;
};

const getElementType = (subtype: PointElementSubtype) => {
  switch (subtype) {
    case 'NETZVERTEILER':
      return organizationManagementApi.NetworkNodeType.NVT;
    case 'HAUPTKABEL':
      return organizationManagementApi.NetworkNodeType.HK;
    case 'POINT_OF_PRESENCE_REGIO':
      return organizationManagementApi.NetworkNodeType.POP;
    case 'BACKBONE':
      return organizationManagementApi.NetworkNodeType.BB;
    case 'ABSCHLUSSPUNKT_LINIENTECHNIK':
      return organizationManagementApi.NetworkNodeType.APL;
    default:
      return organizationManagementApi.NetworkNodeType.UNSPECIFIED;
  }
};

export const PointElementProperties = ({
  ...props
}: ElementDialogProps<PointElement> & {
  onNext: (editedPointElement: PointElement) => void;
}) => {
  const { isTelekomProject } = useIsTelekomProject();
  const {
    element,
    isAddressValid,
    updateProp,
    onSubmit,
    namingScheme,
    hasNameError,
    hasChildren,
    doesElementExistWithSameName,
  } = useConnect({ elementToUpdate: props.elementToUpdate });

  const { orderNumbers } = useOrderNumbers();

  const enchancedAutocompleteOptions = extractFlatMappings(orderNumbers || []).map((orderItem) => {
    return {
      ...orderItem,
      name: orderItem.name.replace(/^dbb_/, ''),
    };
  });

  const { materials } = useMaterials({ subType: element.subtype });

  const { elementToUpdate, onClose, onNext } = props;
  const isAddressRequired = !(
    element.subtype === 'HAUPTKABEL' ||
    element.subtype === 'KABELSCHACHT' ||
    element.subtype === 'BACKBONE' ||
    element.subtype === 'JUNCTION_BOX' ||
    element.subtype === 'POLE' ||
    element.subtype === 'PIT' ||
    element.subtype === 'GLASFASER_MUFFE'
  );

  const showCommentField =
    element.subtype === 'KABELSCHACHT' ||
    element.subtype === 'JUNCTION_BOX' ||
    element.subtype === 'POLE' ||
    element.subtype === 'PIT';

  const isMaterialRequired =
    (element.subtype === 'NETZVERTEILER' || element.subtype === 'GLASFASER_MUFFE') &&
    materials.length > 0;

  const isHeightRequired = element.subtype === 'POLE';
  const hasHeightError = isHeightRequired && element.height === undefined;

  const isPit = element.subtype === 'PIT';
  const hasPitError =
    isPit &&
    (element.width === undefined ||
      element.depth === undefined ||
      element.length === undefined ||
      !element.surfaceType);

  const canSubmit = !hasNameError && isAddressValid && !hasHeightError && !hasPitError;

  const mappedElementType = getElementType(element.subtype);
  const warningMessage = doesElementExistWithSameName(!!elementToUpdate, element)
    ? `WARNING: Element with this name already exists. Are you sure you want to ${
        elementToUpdate ? 'update' : 'create'
      } it?`
    : undefined;

  const isNvtOrHkOrBackboneOrApl =
    element.subtype === 'NETZVERTEILER' ||
    element.subtype === 'HAUPTKABEL' ||
    element.subtype === 'BACKBONE' ||
    element.subtype === 'ABSCHLUSSPUNKT_LINIENTECHNIK';

  const getSubmitButtonLabel = () =>
    (elementToUpdate ? 'Update ' : 'Add ') + toHumanReadable(element.kind);

  const getCreateButtonLabel = () => (elementToUpdate ? 'Update' : 'Create');

  const handleSubmit = async (goToSMAs: boolean) => {
    const nextElement = await onSubmit();

    if (!nextElement) {
      return;
    }

    if (goToSMAs) {
      onNext(nextElement);
      return;
    }

    onClose();
  };

  const comment = extractCommentFromCommentField(element.comment);
  const isNoGeoChecked = extractNoGeoFromCommentField(element.comment);

  const makeChipLabel = (label: PointElementSubtype | undefined) => {
    if (label) {
      const abbreviation = pointElementSubtypeAbbreviations.get(label);
      if (abbreviation) {
        return `${toHumanReadable(label)} (${abbreviation})`;
      }
    }

    return toHumanReadable(label);
  };

  return (
    <>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid size={12} hidden={!!hasChildren} container gap={1}>
            {pointElementSubtype.map((s) => (
              <Chip
                label={makeChipLabel(s)}
                key={s}
                onClick={() => updateProp('subtype', s)}
                color={s === element.subtype ? 'primary' : 'default'}
                variant={s === element.subtype ? 'filled' : 'outlined'}
              />
            ))}
          </Grid>
          <Grid size={12}>
            {enchancedAutocompleteOptions.length ? (
              <Autocomplete
                options={enchancedAutocompleteOptions.filter(
                  (option) => option.type === mappedElementType,
                )}
                getOptionLabel={(option) => option.name ?? ''}
                renderOption={(props, option, state) => (
                  <Fragment key={option.orderNumber + option.name}>
                    {state.index === 0 && (
                      <Stack
                        display="flex"
                        direction="row"
                        justifyContent="space-between"
                        justifyItems="center"
                        alignItems={'center'}
                        gap={1}
                        width="100%"
                        paddingX={2}
                        color="lightgray"
                      >
                        <span>Name</span>
                        {element.subtype === 'NETZVERTEILER' && <span>HK</span>}
                        {element.subtype === 'ABSCHLUSSPUNKT_LINIENTECHNIK' && <span>NVT</span>}
                        {element.subtype === 'POINT_OF_PRESENCE_REGIO' && <span>POP</span>}
                        <span>SMA</span>
                      </Stack>
                    )}
                    <ListItem
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...props}
                      disableGutters
                      sx={{ display: 'flex', flexDirection: 'column' }}
                    >
                      <Stack
                        display="flex"
                        direction="row"
                        justifyContent="space-between"
                        alignItems={'center'}
                        gap={1}
                        width="100%"
                      >
                        <strong>{option.name}</strong>
                        {element.subtype === 'NETZVERTEILER' && <span>{option.hkNumber}</span>}
                        {element.subtype === 'ABSCHLUSSPUNKT_LINIENTECHNIK' && (
                          <span>{option.nvtNumber}</span>
                        )}
                        {element.subtype === 'POINT_OF_PRESENCE_REGIO' && (
                          <span>{option.popNumber}</span>
                        )}
                        <span>{option.orderNumber}</span>
                      </Stack>
                    </ListItem>
                  </Fragment>
                )}
                componentsProps={{ paper: { elevation: 24 } }}
                noOptionsText="No available element names found"
                inputValue={element.name}
                onChange={(_, nextOption, reason) => {
                  if (reason === 'selectOption') {
                    updateProp('name', nextOption?.name);
                  }
                }}
                onInputChange={(e, newInputValue) => {
                  if (!e || e.type === 'blur') return; // avoid erase of input value on initial rendering
                  updateProp('name', newInputValue);
                }}
                renderInput={(renderParams) => (
                  <NameTextField
                    element={element}
                    setName={(nextVal) => updateProp('name', nextVal)}
                    namingScheme={namingScheme}
                    hasNameError={hasNameError}
                    warningMessage={isNvtOrHkOrBackboneOrApl ? warningMessage : undefined}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...renderParams}
                  />
                )}
              />
            ) : (
              <NameTextField
                element={element}
                setName={(nextVal) => updateProp('name', nextVal)}
                namingScheme={namingScheme}
                hasNameError={hasNameError}
                warningMessage={isNvtOrHkOrBackboneOrApl ? warningMessage : undefined}
              />
            )}
          </Grid>
          <Grid size={12}>
            <ParentSelectAutocomplete elementToUpdate={elementToUpdate} updateProp={updateProp} />
          </Grid>
          {isMaterialRequired && (
            <Grid size={12}>
              <MaterialSelectAutocomplete
                subType={element.subtype}
                updateProp={updateProp}
                currentMaterialId={element.materialId}
                materials={materials}
              />
            </Grid>
          )}
          <Grid size={10}>
            <TextField
              required={isAddressRequired}
              fullWidth
              inputProps={{ 'aria-label': 'Street' }}
              label="Street"
              value={element.address.street || ''}
              onChange={(e) => updateProp('address.street', e.target.value)}
            />
          </Grid>
          <Grid size={2}>
            <TextField
              required={isAddressRequired}
              fullWidth
              label="Nr."
              inputProps={{ 'aria-label': 'Number' }}
              value={element.address.number ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  updateProp('address.number', undefined);
                  return;
                }
                if (isNaN(Number(value))) return;
                updateProp('address.number', Number(value));
              }}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              inputProps={{ 'aria-label': 'Addition' }}
              label="Addition"
              value={element.address.addition || ''}
              onChange={(e) => updateProp('address.addition', e.target.value)}
            />
          </Grid>
          <Grid size={3}>
            <TextField
              required={isAddressRequired}
              fullWidth
              inputProps={{ 'aria-label': 'Zip' }}
              label="Zip"
              value={element.address.zip || ''}
              onChange={(e) => updateProp('address.zip', e.target.value)}
            />
          </Grid>
          <Grid size={9}>
            <TextField
              required={isAddressRequired}
              fullWidth
              inputProps={{ 'aria-label': 'City' }}
              label="City"
              value={element.address.city || ''}
              onChange={(e) => updateProp('address.city', e.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              inputProps={{ 'aria-label': 'District' }}
              label="District"
              value={element.address.district || ''}
              onChange={(e) => updateProp('address.district', e.target.value)}
            />
          </Grid>
          {showCommentField && (
            <Grid size={12}>
              <TextField
                fullWidth
                inputProps={{ 'aria-label': 'Comment' }}
                label="Comment"
                value={comment || ''}
                onChange={(e) =>
                  updateProp('comment', getCommentToAdd(isNoGeoChecked, e.target.value))
                }
              />
            </Grid>
          )}
          {element.subtype === 'POLE' && (
            <Grid size={12}>
              <TextField
                required
                fullWidth
                inputProps={{ 'aria-label': 'Height' }}
                label="Height"
                value={element.height || ''}
                onChange={(e) => updateProp('height', e.target.value)}
              />
            </Grid>
          )}
          {element.subtype === 'PIT' && (
            <>
              <Grid size={12}>
                <TextField
                  fullWidth
                  required
                  label="Length (cm)"
                  inputProps={{ 'aria-label': 'Length' }}
                  value={element.length ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '') {
                      updateProp('length', undefined);
                      return;
                    }
                    const num = parseInt(val, 10);
                    if (!isNaN(num)) {
                      updateProp('length', num);
                    }
                  }}
                />
              </Grid>
              <Grid size={12}>
                <Autocomplete
                  options={widths}
                  getOptionLabel={(option) => toHumanReadable(option)}
                  value={widths.find((w) => w === `SIZE_${element.width}_CM`) || 'NOT_APPLICABLE'}
                  onChange={(_, newValue) => {
                    const match = newValue?.match(/\d+/);
                    updateProp('width', match ? parseInt(match[0], 10) : undefined);
                  }}
                  renderInput={(params) => (
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    <TextField {...params} label="Width" fullWidth required />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <Autocomplete
                  options={depthsSorted}
                  getOptionLabel={(option) => toHumanReadable(option)}
                  value={
                    depthsSorted.find((d) => d === `SIZE_${element.depth}_CM`) || 'NOT_APPLICABLE'
                  }
                  onChange={(_, newValue) => {
                    const match = newValue?.match(/\d+/);
                    updateProp('depth', match ? parseInt(match[0], 10) : undefined);
                  }}
                  renderInput={(params) => (
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    <TextField {...params} label="Depth" fullWidth required />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <Autocomplete
                  options={surfaceTypes}
                  getOptionLabel={(option) => toHumanReadable(option)}
                  value={element.surfaceType || null}
                  onChange={(_, newValue) => updateProp('surfaceType', newValue)}
                  renderInput={(params) => (
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    <TextField {...params} label="Surface" fullWidth required />
                  )}
                />
              </Grid>
            </>
          )}
          {element.smaNumber && (
            <Grid size={12}>
              <Typography variant="body2" color="secondary">
                SMA: {element.smaNumber}
              </Typography>
            </Grid>
          )}

          <Grid size={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isNoGeoChecked}
                  onChange={(e) =>
                    updateProp('comment', getCommentToAdd(e.target.checked, comment))
                  }
                />
              }
              label="No Geo"
            />
          </Grid>

          <Grid size={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!element?.labelingConfidence}
                  onChange={(e) =>
                    updateProp('labelingConfidence', e.target.checked ? 'BEST_GUESS' : null)
                  }
                />
              }
              label="Best guess"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions disableSpacing>
        <Stack width="100%" p={2}>
          {isTelekomProject ? (
            <Stack direction="row" gap={2} alignItems="center">
              <Button
                onClick={() => handleSubmit(true)}
                variant="outlined"
                disabled={!canSubmit}
                data-testid="assign-smas-button"
              >
                Assign SMAs
              </Button>
              <Box component="div" flexGrow={1} />
              <Button onClick={onClose} variant="outlined" color="secondary">
                Cancel
              </Button>
              <Button
                onClick={() => handleSubmit(false)}
                variant="contained"
                disabled={!canSubmit}
                data-testid="submit-button"
              >
                {getCreateButtonLabel()}
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" gap={2} justifyContent="flex-end">
              <Button onClick={onClose} variant="outlined" color="secondary">
                Cancel
              </Button>
              <Button
                onClick={() => handleSubmit(false)}
                variant="contained"
                disabled={!canSubmit}
                data-testid="submit-button"
              >
                {getSubmitButtonLabel()}
              </Button>
            </Stack>
          )}
        </Stack>
      </DialogActions>
    </>
  );
};
